/**
 * JobDetailPage.js — /jobs/:id
 *
 * Displays full job details and the user's fit analysis (if any).
 * Provides an "Analyze Fit" button that calls the NDOR backend.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { apiFetch } from '@/lib/api';
import { runAnalyzeFitWithPolling } from '@/lib/analyseFitPolling';
// CWS-1 (operator-locked, 2026-02-15) — public helpers for re-attach
// recovery. `pollAnalyseRun` resumes polling on a known analyse_run
// id without re-dispatching / re-charging; `synthesiseCanonical`
// normalises the terminal envelope into the canonical
// { score, analysis, billing } shape the rest of this page consumes
// (same call site as the sync 200 fast path).
import { pollAnalyseRun, synthesiseCanonical } from '@/lib/analyseFitPolling';
// CWS-3 (operator-locked, 2026-02-15) — backend-authoritative mount
// reconcile against `/api/operations/me/active`. When the operations
// mirror-write (Tier 1 / S3) has landed a row for this (job,
// analyse_fit), the hook's `onRecovering` callback fires with the
// row's `result_envelope_ref` (= analyse_run_id). The handler then
// resumes polling via `pollAnalyseRun` and binds the canonical
// result. Closes the audit's P1 — tab/section switch mid-poll no
// longer orphans the run.
import { useOperationMountReconcile } from '@/lib/operationController/useOperationMountReconcile';
// RC 1.52 Phase 3B.1 — UX cooldown for Analyse Fit CTA so a stale
// wallet + active button doesn't tempt accidental double-charges.
import {
  startCooldown,
  analyseFitCooldownKey,
  useCooldown,
} from '@/lib/actionCooldown';
import {
  writeInflightAnalyzeFit,
  readInflightAnalyzeFit,
  clearInflightAnalyzeFit,
  hasFreshInflightAnalyzeFit,
  hasExpiredInflightAnalyzeFit,
  humaniseAnalyzeFitError,
} from '@/lib/inflightAnalyzeFit';
// RC 1.57 — Generation Session Guard. Persists an idempotency key
// across tab-switches / remounts so a stray re-dispatch can never
// mint a fresh chargeable lifecycle. Recovery paths read-only.
import {
  getOrMintSession,
  markTerminalSuccess,
  markHydrationStable,
  releaseSessionIfStable,
} from '@/lib/generationSession';
import { handleCreditsError } from '@/lib/handleCreditsError';
import { useCredits } from '@/lib/creditsContext';
import { useArtefactRehydrate } from '@/lib/operationController/useArtefactRehydrate';
import { invalidate as cacheInvalidate, cachedFetch, peek, KEYS } from '@/lib/listCache';
import { isWizardOpen, markWizardOpen, clearWizardOpen } from '@/lib/wizardState';
import WorkspaceApplyEntryCard from '@/components/WorkspaceApplyEntryCard';
// RC 1.58 Phase 3E (operator-locked, 2026-02-14) — Workspace
// Session Guidance modal. Frontend-only entry-point acknowledgement
// gate. Wraps the "Analyse Fit" click so the calm informational
// guidance appears ONCE per browser session before generation
// begins. The same hook also gates the "Open Workspace" CTA via
// `WorkspaceApplyEntryCard` (already wired in Phase 4). Acknowledgement
// is shared across both entry surfaces — sessionStorage key
// `workspace:ack:shown`. No backend / orchestration / billing /
// idempotency / generation-state change.
import {
  useWorkspaceAcknowledgement,
  WorkspaceAcknowledgementDialog,
} from '@/components/workspace/WorkspaceAcknowledgement';
// RC 1.49.7 Phase 0 — Application Workspace feature flag. When
// enabled, Apply routes the user to /applications/workspace/:jobId
// instead of opening the legacy WizardEntryNotice + modal wizard.
// Default OFF. RC 1.49.7 C8 (2026-02) — switched to the
// operator-aware variant so the operator can soak the workspace
// via a localStorage override without a global flag flip.
import {
  isWorkspaceModeEnabledForUser,
} from '@/lib/workspaceFeatureFlag';
import { useIsOperator } from '@/lib/useIsOperator';
import PriceLabel from '@/components/PriceLabel';
import AnalyzeFitCost from '@/components/AnalyzeFitCost';
import JobDescription from '@/components/JobDescription';
import SourceCVSelect, { decodeSource } from '@/components/SourceCVSelect'; // RC 1.42 P0.2 — `decodeSource` retained for now (other consumers); SourceCVSelect no longer rendered on this page
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  ArrowDown, ArrowLeft, Briefcase, MapPin, Building2, Clock, ExternalLink,
  Loader2, Sparkles, AlertCircle, Pencil, Save, X, Info, CheckCircle,
} from 'lucide-react';
import FitResultCard from '@/components/FitResultCard';
// RC 1.50 polish-clarification — InterviewPrepCard import retired.
// The base Analyse Fit / Job Detail page is diagnostic only and
// must not surface Workspace Interview Prep content here. Keeping
// the import-removed line documented so a future agent doesn't
// reintroduce the mount.
import JobApplicationWizard from '@/components/wizard/JobApplicationWizard';
import {
  ApplyFlowClarifierModal,
  shouldShowApplyClarifier,
} from '@/components/wizard/ApplyFlowClarifierModal';
import { WizardEntryNotice } from '@/components/wizard/WizardEntryNotice';
import UsageExhaustedModal from '@/components/UsageExhaustedModal';
// RC 1.49.x reviewer-trust polish — NextStepStrip import removed
// alongside its render. Retired from JobDetailPage; the workflow
// mental model lives on the first-run splash + How It Works.
import AnalyseFitExplainer from '@/components/render/AnalyseFitExplainer';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';

// Mirrors JobApplicationWizard.jsx — once per session via shared sentinel.
function fireFirstActionOnce(actionType) {
  try {
    const k = 'ndor_dashboard_first_action_fired';
    if (sessionStorage.getItem(k) === '1') return;
    track('dashboard_first_action', { action_type: actionType });
    sessionStorage.setItem(k, '1');
  } catch { /* never throw from telemetry */ }
}

const EDITABLE_FIELDS = [
  'title', 'company', 'location', 'employment_type', 'seniority_level',
  'description', 'requirements_text', 'salary_currency', 'url',
];

export default function JobDetailPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // RC 1.49.x Stabilisation Slice 1 — `?imported=1` surfaces a
  // one-time confirmation banner when the user lands here straight
  // from a successful import in AddJobDialog. Dismissed locally + by
  // stripping the query param so reload / back / link sharing don't
  // re-trigger it. The flag does NOT auto-trigger Analyze Fit; that
  // remains user-initiated to preserve consent for any credit charge.
  const importedFlag = searchParams.get('imported') === '1';
  const [importedBannerOpen, setImportedBannerOpen] = useState(importedFlag);
  useEffect(() => {
    if (importedFlag) setImportedBannerOpen(true);
  }, [importedFlag]);
  const dismissImportedBanner = useCallback(() => {
    setImportedBannerOpen(false);
    // Strip the query param so a refresh / back-nav doesn't re-pop it.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('imported');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  const { user } = useAuth();
  // RC 1.62 Phase D completion (D-3) — operator flag is now
  // server-projected via `/api/auth/me/access::is_operator`. The
  // frontend no longer carries a hardcoded UUID/email.
  const { isOperator } = useIsOperator();
  // RC 1.58 Phase 3E (operator-locked, 2026-02-14) — Workspace
  // Session Guidance gate for the Analyse Fit entry. Shows the
  // calm informational modal once per browser session before the
  // first generation-bearing click on this page. Subsequent clicks
  // (including the "Re-analyse Fit" relabel of the same button)
  // bypass the modal because the sessionStorage flag is already
  // set. Strict hold: no orchestration / billing / idempotency
  // touch — `guardedAction` simply defers the existing
  // Analyse-Fit callback until the user clicks Continue.
  const {
    guardedAction: guardedAnalyseFitAction,
    dialogProps: workspaceAckDialogProps,
  } = useWorkspaceAcknowledgement();
  // RC 1.38.B.1 — read the REAL usage entitlement from
  // /api/account/plan (NOT legacy /api/credits/me wallet balance).
  const {
    refresh: refreshCredits,
    includedUsageRemaining,
    extraUsageRemaining,
  } = useCredits();
  const usageTotalRemaining =
    (typeof includedUsageRemaining === 'number' ? includedUsageRemaining : 0)
    + (typeof extraUsageRemaining === 'number' ? extraUsageRemaining : 0);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  // RC 1.55 Phase 3 (operator-locked, 2026-05-13) — STABILISATION
  // phase. True while the post-LLM side effects (refreshCredits +
  // loadFitUsage + baseline-fit GET) are settling. Mirrors the
  // CL/IP/CV controller's `stabilizing` phase so the page-level
  // activity strip + CTA lock stay engaged until the result is
  // fully bound and the wallet pill has refreshed. Cleared
  // atomically with `analyzing` in the handler's `finally`.
  const [stabilising, setStabilising] = useState(false);
  // CWS-1 (2026-02-15, operator-locked) — separate "recovering"
  // state for the read-only re-attach to an in-flight analyse_run
  // that survived a tab/section switch. Distinct from `analyzing`
  // so the Generate / Analyse Fit button STAYS ENABLED while we
  // silently re-attach + finalise polling. Mirrors the IP section's
  // `recovering` state (S7 patches). Drives banner-only UI; never
  // disables the chargeable Generate button. See
  // CANDIDATE_WORKSPACE_LRO_AUDIT.md §F (CWS-1).
  const [recoveringAnalyseFit, setRecoveringAnalyseFit] = useState(false);
  // RC 1.49.x Slice 2B — rehydration state machine for the
  // Analyse Fit in-flight marker. Drives the calm "Still checking
  // your analysis…" strip + the expired-marker retry hint shown
  // right above the Analyse Fit card.
  //   null              — no marker (default).
  //   'checking'        — marker exists, refetch in flight.
  //   'inflight'        — marker exists and is fresh, but the
  //                       refetch returned no score yet.
  //   'recovered'       — marker existed, refetch returned a score
  //                       (cleared just after surfacing toast).
  //   'expired'         — marker existed but TTL elapsed.
  const [rehydrationStatus, setRehydrationStatus] = useState(null);
  const [error, setError] = useState('');

  // Edit-mode state.
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);

  // RC 1.19 — Analyze Fit with Source CV
  const [uploadedCvs, setUploadedCvs] = useState([]);
  const [variants, setVariants] = useState([]);
  // RC 1.38.A.UI.FLOW — used by NextStepStrip to compute done/pending
  // state for steps 3 + 4 (Cover Letter + Application Pack).
  const [letters, setLetters] = useState([]);
  const [packs,   setPacks]   = useState([]);
  const [fitSource, setFitSource] = useState(''); // RC 1.42 P0.2 — retained but UNUSED for Analyse Fit (baseline now uses primary CV exclusively); kept to avoid a wider refactor in this slice
  // RC 1.42 P0.2 — baseline trust state (primary CV + cached baseline + stale row).
  // Replaces the prior CV-source dropdown. Loaded via
  // `GET /api/jobs/{id}/baseline-fit` (pure read; no LLM, no charge).
  const [baselineFit, setBaselineFit] = useState(null);
  // RC 1.52 Phase 3B.1 — sourceId-scoped Analyse Fit cooldown.
  // Tied to (user, job, primary_cv.id) so a baseline cooldown
  // doesn't lock a future variant-targeted Analyse Fit run.
  const analyseFitCooldown = useCooldown(analyseFitCooldownKey(
    user?.id, id, baselineFit?.primary_cv?.id || 'baseline',
  ));
  // RC 1.41 Phase D — application wizard overlay. Replaces the
  // legacy StartApplicationPackDialog as the application entry
  // point. The dialog stays mounted so existing screenshot tests
  // don't break, but the primary CTA opens the wizard instead.
  const [wizardOpen, setWizardOpen] = useState(false);
  // RC 1.49.7 Slice 6 (operator P0, 2026-02) — inline workspace
  // entry card. When `isWorkspaceModeEnabledForUser(user)` is true,
  // clicking Apply expands an inline non-floating entry card on
  // this page (not the workspace). The card explains the four
  // optional steps, surfaces costs from `useCredits().usageCosts`,
  // and offers a single confirm CTA that navigates to
  // `/applications/workspace/:id`. Cancel collapses the card. State
  // is intentionally local — no localStorage, no wizardState.js
  // marker, no resume semantics. The workspace re-entry rule is
  // "URL is the state".
  const [workspaceEntryOpen, setWorkspaceEntryOpen] = useState(false);
  // RC 1.49.4 L-1 (operator-locked, 2026-05-08) — auto-reopen the
  // wizard on remount when there is an active session for this job.
  // The marker (set on open, cleared on close/finish) is persisted
  // in localStorage so it survives page refresh AND full browser
  // close. Stale markers are bounded by a 24h TTL inside
  // `isWizardOpen`. No new endpoint, no charge implication.
  useEffect(() => {
    if (!id) return;
    if (isWizardOpen(id)) setWizardOpen(true);
  }, [id]);
  // Mirror the React open state into the persistent marker so
  // every entry path (CTA click, clarifier-resume, deep link)
  // benefits without needing per-call wiring.
  useEffect(() => {
    if (!id) return;
    if (wizardOpen) markWizardOpen(id);
    else clearWizardOpen(id);
  }, [id, wizardOpen]);
  // ── RC 1.49.x Phase 3A — Apply Flow Clarifier (operator-locked) ──
  // First Apply click in a session opens the clarifier modal; only
  // after the user clicks "Continue to wizard" does the wizard open.
  // The clarifier itself sets the dismiss flag so subsequent clicks
  // open the wizard directly.
  const [clarifierOpen, setClarifierOpen] = useState(false);
  // RC 1.49.6 (operator-locked, 2026-05-08) — Wizard Entry Notice.
  // Shown EVERY user-initiated wizard launch (Apply CTA, sticky
  // mobile CTA, post-clarifier Continue). Specifically NOT shown on
  // the auto-reopen path (RC 1.49.4 L-1 marker), because at that
  // point work may already be in flight and the entry guidance
  // would be confusing rather than useful.
  const [entryNoticeOpen, setEntryNoticeOpen] = useState(false);
  const navigate = useNavigate();
  const requestApply = () => {
    // RC 1.49.7 Phase 0 — Application Workspace flag. When ON,
    // bypass the clarifier + entry-notice + modal-wizard chain and
    // navigate to the route-based workspace. The workspace's own
    // section landing already replaces the entry-notice's job of
    // setting expectations. All RC 1.49.x billing / pricing /
    // idempotency / replay / cache-hit / Interview Pack rules are
    // preserved by the workspace's Phase 1-3 implementations.
    // Default OFF — wizard remains the live experience.
    //
    // RC 1.49.7 C8 (2026-02) — `isWorkspaceModeEnabledForUser(user)`
    // honours the operator-only `__ndorflow_workspace_override`
    // localStorage soak override (operator id + email gated; no
    // effect on any other user). Also fixed the latent
    // `${jobId}` → `${id}` typo on the navigate target — `useParams`
    // returns `id`, not `jobId`, so the global cutover would have
    // landed on `/applications/workspace/undefined`.
    // RC 1.49.7 Slice 6 (operator P0, 2026-02) — when workspace
    // mode is on, expand the inline entry card instead of jumping
    // straight to the workspace. The card itself owns the navigate
    // call (on confirm). Cancel collapses the card with no state
    // leak. Pre-Slice-6 this branch jumped to the workspace
    // immediately, bypassing the entry-notice UX wizard users
    // still see.
    if (isWorkspaceModeEnabledForUser(isOperator)) {
      setWorkspaceEntryOpen(true);
      return;
    }
    if (shouldShowApplyClarifier()) {
      setClarifierOpen(true);
    } else {
      setEntryNoticeOpen(true);
    }
  };
  // RC 1.41 Phase A — `freeFit` (monthly uploaded-CV allowance) is
  // gone. The dual allowance (monthly + daily) confused users and
  // was the trigger for the `wallet_missing` raw error path. The
  // remaining surface uses USAGE_COSTS and the daily Base quota.

  // RC 1.31 — Free-tier daily Analyze-Fit usage.
  const [fitUsage, setFitUsage] = useState(null);
  // RC 1.33 — Plan + usage balance for Focus/Flow users.
  const [accountPlan, setAccountPlan] = useState(null);
  const [usageExhaustedOpen, setUsageExhaustedOpen] = useState(false);
  // RC 1.34 — per-open plan_code so the modal can switch between
  // Base and Focus/Flow copy. Default 'focus' matches pre-RC 1.34.
  const [usageExhaustedPlan, setUsageExhaustedPlan] = useState('focus');

  // RC 1.46 — Source CV deterministic-score state retired. The
  // page now reads ONLY the LLM-graded `cv_sub_score` from
  // `job.user_score.reasoning_json` (set by Analyse Fit). The
  // FitResultCard ref is used to auto-scroll to the breakdown the
  // moment scoring completes.
  const fitBreakdownRef = useRef(null);
  // RC 1.48.1 — abort controller for in-flight 202-path polling.
  // Cancels the polling loop when the page unmounts, when navigation
  // away from this job occurs, or when the user clicks Analyse Fit
  // again (a fresh analysis supersedes any prior in-flight poll).
  const analysePollAbortRef = useRef(null);
  const loadFitUsage = useCallback(async () => {
    try {
      // RC 1.59 Slice 3 — cache the two read-only meta endpoints
      // so re-entering /jobs/:id within the TTL window doesn't
      // re-hit the network. Invalidated explicitly after every
      // mutation that can change them (Analyse Fit success below
      // bumps both via `loadFitUsage()` post-invalidate).
      const [u, p] = await Promise.all([
        cachedFetch(
          KEYS.analyzeFitUsage(),
          () => apiFetch('/api/jobs/analyze-fit/usage'),
          { ttlMs: 60_000 },
        ),
        cachedFetch(
          KEYS.accountPlan(),
          () => apiFetch('/api/account/plan').catch(() => null),
          { ttlMs: 60_000 },
        ),
      ]);
      setFitUsage(u || null);
      setAccountPlan(p || null);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { loadFitUsage(); }, [loadFitUsage]);

  /** Load job data. */
  const loadJob = useCallback(async () => {
    try {
      // RC 1.59 Slice 3 — cache the canonical job-by-id read. 5-min
      // TTL with stale-while-revalidate; invalidated after PATCH
      // and after Analyse Fit completion. The explicit post-
      // mutation refetches at L676 / L862 remain raw `apiFetch`
      // (cache bypass, per operator) — they're called AFTER an
      // explicit `cacheInvalidate(KEYS.jobById(id))` so the next
      // mount sees fresh state.
      const data = await cachedFetch(
        KEYS.jobById(id),
        () => apiFetch(`/api/jobs/${id}`),
        { ttlMs: 5 * 60_000 },
      );
      setJob(data);
    } catch (err) {
      setError(err.message || 'Failed to load job.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadJob(); }, [loadJob]);

  // RC 1.49.x Slice 3 — ref on the Description card + smooth-scroll
  // helper for the imported-banner "View full job description below"
  // link. Declared up here (before any early returns) so the hooks
  // run in a consistent order on every render.
  const descriptionCardRef = useRef(null);
  const scrollToDescription = useCallback(() => {
    descriptionCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // RC 1.49.x Slice 2B — Analyse Fit rehydration on mount + on
  // tab return. If a localStorage in-flight marker exists for
  // (this user, this job), re-fetch /api/jobs/{id} once and pull
  // the user_score the backend may have written while we were
  // gone. Operator-locked rules:
  //
  //   • One-shot per visibility cycle. A `cooldownRef` prevents
  //     duplicate refetches if visibilitychange fires repeatedly.
  //   • No new endpoint, no polling; we reuse the same /api/jobs/{id}
  //     read that mount already does.
  //   • If the marker has expired (TTL = 5min), we clear it and
  //     surface a calm retry hint via `rehydrationStatus = 'expired'`.
  //   • If the refetch returns a score → setJob, refresh credits,
  //     clear marker, surface a one-line success toast, set status
  //     to 'recovered' (then null after a tick so the strip fades).
  //   • If the refetch returns no score yet → set status to
  //     'inflight' so the strip says "Still checking…".
  //   • The user's `analyzing` button-spinner state is independent —
  //     we don't toggle it here. The user may have already left
  //     the page and come back; what matters is the result.
  const rehydrateLockRef = useRef(false);
  const rehydrateAnalyseFit = useCallback(async () => {
    if (!user?.id || !id) return;
    if (rehydrateLockRef.current) return;
    if (analyzing) return;  // active polling will handle the result.
    if (job?.user_score) {
      // Already have a score in local state — nothing to recover.
      // If a stale marker is still on disk, clean it up.
      if (readInflightAnalyzeFit(user.id, id)) {
        clearInflightAnalyzeFit(user.id, id);
        setRehydrationStatus(null);
      }
      return;
    }
    if (hasExpiredInflightAnalyzeFit(user.id, id)) {
      clearInflightAnalyzeFit(user.id, id);
      setRehydrationStatus('expired');
      return;
    }
    if (!hasFreshInflightAnalyzeFit(user.id, id)) {
      // No marker → nothing to do.
      return;
    }
    rehydrateLockRef.current = true;
    setRehydrationStatus('checking');
    try {
      const fresh = await apiFetch(`/api/jobs/${id}`);
      if (fresh?.user_score) {
        setJob(fresh);
        clearInflightAnalyzeFit(user.id, id);
        setRehydrationStatus('recovered');
        // Refresh credits so the post-charge balance is visible.
        try { refreshCredits(); } catch { /* never throw */ }
        // RC 1.55 Phase 1 (operator-locked, 2026-05-13) —
        // invalidate the dashboard's jobs list / bundle cache so
        // the new user_score is reflected on next mount of any
        // surface that derives off the jobs list (queues, filters,
        // counters). READ-ONLY — no POST, no charge. Fail-open.
        try { cacheInvalidate(['jobs', 'dashboard:bundle']); }
        catch { /* noop */ }
        toast.success('Your fit analysis was completed in the background.');
        // Fade the strip out on the next tick.
        setTimeout(() => setRehydrationStatus(null), 4000);
      } else {
        // Backend hasn't written the score yet; keep the marker
        // and surface the calm "still checking" copy. The next
        // visibility cycle will retry.
        //
        // RC 1.55 Phase Z.1 (operator-locked, 2026-05-13) — bound
        // the "still checking…" window to ~90 s. Typical Analyse
        // Fit completes inside 60 s; if the canonical GET returns
        // `user_score=null` after that envelope it almost always
        // means the backend dispatch failed silently (e.g. the
        // Supabase `job_scores` write timed out and the run was
        // PATCHed to `status='failed'` by Phase Z.1's transactional
        // guard). Surface the calm 'expired' copy instead of looping
        // 'inflight' until the 5-min TTL elapses. The marker is
        // cleared so the next click starts from a clean slate. No
        // toast — the user is on the page and already saw the
        // dispatch-time error toast (Phase Z.1 503 path); this is
        // pure cleanup for the navigated-away mid-spin case.
        const marker = readInflightAnalyzeFit(user.id, id);
        // RC 1.60.P0C (operator-approved 2026-02-15) — marker-null
        // race guard. If a concurrent `handleAnalyzeFit` success path
        // cleared the marker (L769) while our `/api/jobs/{id}` GET
        // above was in flight, there is no in-flight run left to
        // surface. Clear the rehydration status and return — the
        // dispatch path's own `setRehydrationStatus(null)` at L770
        // owns the UI. Pre-fix the else-branch fell through to
        // `setRehydrationStatus('inflight')` with `ageMs = 0`, which
        // OVERWROTE the dispatch's null and locked the page in a
        // perpetual "Analysis is still updating…" state until manual
        // refresh. See `/app/test_reports/` audit + Node pin
        // `rc_1_60_p0c_marker_null_race.node.test.js`.
        if (!marker) {
          setRehydrationStatus(null);
          return;
        }
        const ageMs = Date.now() - (marker.startedAtMs || 0);
        if (ageMs >= 90 * 1000) {
          clearInflightAnalyzeFit(user.id, id);
          setRehydrationStatus('expired');
        } else {
          setRehydrationStatus('inflight');
        }
      }
    } catch {
      // Network blip / 401 — preserve the marker; the next
      // visibility cycle retries. Don't fire a toast.
      setRehydrationStatus('inflight');
    } finally {
      rehydrateLockRef.current = false;
    }
  }, [user?.id, id, analyzing, job?.user_score, refreshCredits]);

  // Run once on mount + whenever the tab returns to visibility.
  // Keyed on `id` so navigating between jobs re-evaluates per-job.
  //
  // RC 1.55 U4 (operator-locked, 2026-05-13) — ported to the shared
  // `useArtefactRehydrate` hook for lifecycle parity with U1 (Cover
  // Letter), U2 (Interview Prep), and U3 (CV Variant). The hook
  // installs the SAME `visibilitychange` listener this effect used
  // to install, and ADDITIONALLY:
  //   • adds a 250 ms debounce so rapid screen-lock / unlock cycles
  //     coalesce into one canonical GET (was previously protected
  //     only by `rehydrateLockRef` inside the callback);
  //   • adds a `popstate` listener so a browser back/forward to
  //     this job page also drives the same READ-ONLY rebind.
  //
  // The `rehydrateAnalyseFit` callback above is UNCHANGED — every
  // existing recovery rule (marker TTL, expired path, fresh-marker
  // GET, 'checking' / 'inflight' / 'recovered' / 'expired' status
  // FSM, toast copy, credits refresh, fade timer) is preserved
  // verbatim. The `rehydrateLockRef` inside the callback is
  // retained as defence-in-depth alongside the hook's own
  // `lockRef` — they compose safely.
  //
  // Hard rails — port adds NO new chargeable surface:
  //   • READ-ONLY canonical GET `/api/jobs/{id}` only.
  //   • No /analyze-fit POST.
  //   • No idempotency-key minting / clearing.
  //   • No scoring change. No billing change. No backend change.
  useArtefactRehydrate({
    enabled: !loading && !!user?.id && !!id,
    fetchAndBind: rehydrateAnalyseFit,
    runOnMount: true,
  });

  // CWS-1 + CWS-3 (operator-locked, 2026-02-15) — backend mount
  // reconcile + resume-polling for Analyse Fit. When the
  // `operations` mirror-write (Tier 1 / S3 in `routes/runs.py`)
  // has landed a row for (this user, this job, analyse_fit) in
  // status='polling', the hook reports `recovering` with the
  // row's `result_envelope_ref` (= analyse_run_id). We resume
  // polling via `pollAnalyseRun(runId)` — a READ-ONLY call that
  // never re-dispatches, never charges, and never disables the
  // Generate button. On terminal success, we bind the score
  // through the same canonical re-fetch path the sync 200 path
  // uses (`setJob(prev => merge)` + `cacheInvalidate(...)` + a
  // calm restore toast).
  //
  // The hook degrades gracefully if the operations table is not
  // yet populated for this op (e.g. dispatch happened pre-
  // migration, or the mirror-write was silently dropped) — the
  // existing `useArtefactRehydrate` path above remains in place
  // as the fallback (it reads the LS marker + GET /jobs/{id}).
  useOperationMountReconcile({
    targetKind: 'job',
    targetId:   id,
    actionCode: 'analyse_fit',
    enabled:    !loading && !!user?.id && !!id,
    apiFetch:   (path, opts) => apiFetch(path, opts),
    onRecovering: async (row) => {
      const runId = row?.result_envelope_ref;
      if (!runId) return;
      // If the user is already actively dispatching (their own
      // click → handleAnalyzeFit awaiting on the polling helper),
      // don't double-attach. The orchestrator's idempotency-key
      // dedupe at the backend would short-circuit the second
      // attach but we save a network roundtrip by gating here.
      if (analyzing || recoveringAnalyseFit) return;
      setRecoveringAnalyseFit(true);
      try {
        // RC 1.55 Phase 3 — abort signal intentionally NOT passed.
        // The CWS-1 contract is "polling continues across unmount";
        // a re-attach must not be cancelled by the same component's
        // unmount. The next mount will reconcile again.
        const polled = await pollAnalyseRun(runId, {});
        if (polled.status === 'failed' || polled.status === 'cancelled') {
          // Backend already wrote a terminal-fail row; nothing for
          // the FE to bind. The next dispatch attempt will get a
          // fresh charge (no double-charge risk — this run already
          // recorded its own ledger entry).
          try { clearInflightAnalyzeFit(user?.id, id); } catch { /* noop */ }
          return;
        }
        const canonical = synthesiseCanonical(polled);
        const score = canonical?.score;
        if (score && typeof score.fit_score !== 'undefined') {
          // Same setJob shape as the sync 200 path; merge the score
          // surface so the breakdown card + decision label re-derive.
          setJob((prev) => ({ ...prev, user_score: score }));
          // Canonical job re-fetch — invalidates cache so the
          // freshly-bound score doesn't shadow stale top-level
          // fields the polled envelope doesn't carry (reasoning_json,
          // final_decision, recommendation, etc.). Fail-open.
          try { cacheInvalidate(KEYS.jobById(id)); } catch { /* noop */ }
          try {
            const fresh = await apiFetch(`/api/jobs/${id}`);
            if (fresh) setJob(fresh);
          } catch { /* peripheral fields may be stale */ }
          // Sweep the same caches the sync 200 path invalidates so
          // queue counters + plan + baseline-fit re-derive.
          try {
            cacheInvalidate([
              'jobs',
              'dashboard:bundle',
              KEYS.analyzeFitUsage(),
              KEYS.accountPlan(),
              KEYS.baselineFit(id),
            ]);
          } catch { /* noop */ }
          if (typeof refreshCredits === 'function') {
            try { await refreshCredits(); } catch { /* noop */ }
          }
          // Marker is now stale — backend run is terminal.
          try { clearInflightAnalyzeFit(user?.id, id); } catch { /* noop */ }
          try {
            toast.success('Your fit analysis was completed in the background.');
          } catch { /* noop */ }
        }
      } catch {
        // Read-only recovery; degraded silently. The user can
        // click Analyse Fit again — idempotency keeps the second
        // click free if it lands within the stabilisation window.
      } finally {
        setRecoveringAnalyseFit(false);
      }
    },
    onTerminal: async (row) => {
      // BE confirms this job's analyse_fit is terminal.
      //
      // CWS-1 HOTFIX (2026-02-15 follow-up) — the prior version of
      // this handler only cleared the LS marker. That left the
      // "ran but didn't bind" bug uncovered: when the user
      // navigated away mid-poll, the FE fetch was orphaned, the BE
      // completed + wrote a 'complete' operations row, then on
      // remount we cleared the marker but never bound the score —
      // forcing the user to click Analyse Fit a second time
      // (which then idempotency-replayed the existing run and
      // bound). That second click is what the operator observed.
      //
      // Fix: if the FE doesn't already hold a bound score for this
      // job, fetch the canonical `/api/jobs/{id}` and bind it. This
      // is the same READ-ONLY rebind the rehydrate effect performs;
      // we fire it here so the operations-table truth ALWAYS
      // converges to FE state on mount.
      try { clearInflightAnalyzeFit(user?.id, id); } catch { /* noop */ }
      try {
        // If the page already has a score (e.g. sync_200 dispatch
        // bound it before navigating), skip the refetch.
        if (job?.user_score) return;
        const fresh = await apiFetch(`/api/jobs/${id}`);
        if (fresh) {
          setJob(fresh);
          // Mirror the same cache invalidation set the sync 200
          // path triggers so the dashboard queue + plan counters
          // re-derive without a manual reload.
          try {
            cacheInvalidate([
              'jobs',
              'dashboard:bundle',
              KEYS.analyzeFitUsage(),
              KEYS.accountPlan(),
              KEYS.baselineFit(id),
            ]);
          } catch { /* noop */ }
          if (fresh.user_score) {
            try {
              toast.success(
                'Your fit analysis was completed in the background.',
              );
            } catch { /* noop */ }
          }
        }
      } catch {
        // Network blip / 401 — degrade silently. The rehydrate
        // effect will retry on the next visibility cycle.
      }
    },
    onNone: () => {
      // BE confirms no in-flight op for this (job, analyse_fit).
      // Any LS marker we hold is stale — clear it.
      try { clearInflightAnalyzeFit(user?.id, id); } catch { /* noop */ }
    },
  });

  // Prefetch uploaded CVs, variants, and free-fit allowance so the
  // Source CV dropdown + helper line render immediately.
  //
  // RC 1.59 Slice 3 — five mount-time reads wrapped via `cachedFetch`
  // so revisits within TTL serve from the module-scoped cache. Same
  // pattern as Slice 2's Documents tabs. Mutations elsewhere (Generate
  // CV / Letter / Interview Prep, Pack create / archive, Source CV
  // replace, Analyse Fit completion) `invalidate(...)` the matching
  // KEY at their existing success blocks (CvSection / CoverLetterSection /
  // InterviewPrepSection / PackSection / CvLibraryPage / this file's
  // Analyse Fit handler). Cache miss → network → fresh result.
  useEffect(() => {
    let live = true;
    (async () => {
      if (!user?.id) return;
      try {
        const cvData = await cachedFetch(
          KEYS.uploadedCvs(user.id),
          async () => {
            const { data, error } = await supabase
              .from('base_cvs')
              .select('id, original_filename, is_primary, parsed_text')
              .eq('user_id', user.id)
              .order('created_at', { ascending: false });
            if (error) throw new Error(error.message);
            return data || [];
          },
          { ttlMs: 5 * 60_000 },
        );
        if (live) setUploadedCvs(cvData || []);
      } catch (_) { /* non-fatal */ }
      try {
        const vs = await cachedFetch(
          KEYS.cvVariantsAll(),
          () => apiFetch('/api/cv-variants'),
          { ttlMs: 5 * 60_000 },
        );
        if (live) setVariants(vs || []);
      } catch (_) { /* non-fatal */ }
      // RC 1.38.A.UI.FLOW — load cover-letters + application-packs
      // for this (user, job) so the NextStepStrip can show accurate
      // done/pending state. These are tiny lists (per-user). No new
      // LLM call, no usage deduction.
      try {
        const ls = await cachedFetch(
          KEYS.coverLettersAll(),
          () => apiFetch('/api/cover-letters'),
          { ttlMs: 5 * 60_000 },
        );
        if (live) setLetters(ls || []);
      } catch (_) { /* non-fatal */ }
      try {
        const ps = await cachedFetch(
          KEYS.applicationPacksAll(),
          () => apiFetch('/api/application-packs'),
          { ttlMs: 5 * 60_000 },
        );
        if (live) setPacks(ps || []);
      } catch (_) { /* non-fatal */ }
      // RC 1.41 Phase A — monthly free-fit allowance fetch removed.
      // RC 1.46 — deterministic Source-CV keyword fetch retired.
      // The score-summary tile now renders ONLY the LLM-graded
      // `cv_sub_score` from `job.user_score.reasoning_json`, so no
      // additional /keyword-fit/cv round-trip is needed on mount.
      // RC 1.42 P0.2 — baseline-fit hydration. Pure read; no charge.
      // Surfaces baseline state for the user's CURRENT primary CV +
      // (when applicable) a stale row from an earlier primary CV.
      try {
        const bf = await cachedFetch(
          KEYS.baselineFit(id),
          () => apiFetch(`/api/jobs/${id}/baseline-fit`).catch(() => null),
          { ttlMs: 5 * 60_000 },
        );
        if (live && bf) setBaselineFit(bf);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { live = false; };
  }, [user?.id, id]);

  /** Trigger NDOR fit analysis for this job.
   *  RC 1.19 — honours the selected Source CV. Cost is read live
   *  from `usageCosts.analyze_fit` (admin-editable DB-backed price);
   *  uploaded-CV analyses use the monthly free-fit allowance first. */
  // RC 1.33 — Plan flags derived from /account/plan.
  // RC 1.33.FIX — balances are clamped client-side so a stale or
  // over-counted backend response can never render negatives.
  const isPaidActive = Boolean(
    accountPlan
    && (accountPlan.plan_code === 'focus' || accountPlan.plan_code === 'flow')
    && accountPlan.status === 'active',
  );
  const paidTotalRemaining = isPaidActive
    ? Math.max(0,
        (accountPlan.included_usage_remaining || 0)
        + (accountPlan.extra_usage_remaining  || 0))
    : null;
  const paidExhausted = isPaidActive && paidTotalRemaining === 0;

  // RC 1.46 — single-source-of-truth derivation for the LLM-graded
  // NDOR CV verdict. Backend stores it inside `reasoning_json` on
  // `job_scores` (the row hydrated as `job.user_score`). Falls back
  // to null when no Analyse Fit baseline exists for this job; the
  // score-summary tile then renders an empty state with the
  // "Run Analyse Fit ↓" CTA.
  const cvSubScore = (() => {
    const raw = job?.user_score?.reasoning_json?.cv_sub_score;
    if (raw == null) return null;
    const v = Number(raw);
    if (!Number.isFinite(v)) return null;
    return v <= 1 ? Math.round(v * 100) : Math.round(v);
  })();

  const handleAnalyzeFit = async () => {
    // RC 1.60.P0D (operator-approved 2026-02-15) — duplicate-charge
    // idempotency guard.
    //
    // Operator forensic trail (2026-02-15) showed two analyse_runs
    // rows for the same (user, job) within 6 minutes, with
    // DIFFERENT idempotency keys, each charging 9 EXTRA. The
    // backend's `credit_ledger.idempotency_key` partial unique
    // index correctly dedupes identical keys — but when the
    // browser receives a 504 from the production CDN on the
    // long-running `POST /api/runs`, the user perceives "it
    // failed", clicks Analyse Fit again, and the second click
    // mints a FRESH idempotency key (because by then the marker /
    // generationSession may have been cleared by visibility +
    // success paths) → backend treats it as a new request → new
    // charge.
    //
    // Mitigation (strict-hold compliant): consult the existing
    // `hasFreshInflightAnalyzeFit` marker (5-min localStorage TTL)
    // BEFORE entering the dispatch lifecycle. If a fresh marker
    // exists, surface a calm toast and short-circuit. The original
    // dispatch's polling helper (or the next tab return / refresh
    // via `useArtefactRehydrate`) will surface the result. No new
    // expiry window invented — uses the existing 5-min TTL contract.
    //
    // STRICT-HOLD: no backend, no billing, no credit-ledger, no
    // scoring, no charge-amount, no cooldown, no schema, no CV /
    // Letter / Prep / Pack changes. Frontend-only short-circuit.
    if (hasFreshInflightAnalyzeFit(user?.id, id)) {
      toast.info(
        "You're already analysing this job — we'll show the result as soon as it completes.",
      );
      return;
    }

    // RC 1.42 P0.2 — baseline Analyse Fit always targets the user's
    // CURRENT primary CV. The CV-source dropdown was removed so users
    // cannot accidentally analyse against a stale or non-primary CV.
    // Primary CV id comes from `baselineFit.primary_cv.id` (loaded on
    // mount). If it is missing, we send an empty body and let the
    // backend fall back to its own primary lookup.
    const primaryId = baselineFit?.primary_cv?.id || null;
    // Client-side guards. Cached re-analyses always pass through
    // (job.user_score is set), so both guards only apply to NEW
    // analyses.
    //
    // RC 1.44.H2 (2026-04-30) — P0 commercial-trust fix.
    // Previously base users were blocked the moment their daily
    // free-tier allowance hit zero, EVEN IF they had top-up credits
    // (top-ups) in their wallet — which violated the core monetisation
    // contract: you pay for credits, you can spend credits. The gate
    // now blocks only when BOTH free-tier AND wallet are empty.
    if (!job?.user_score) {
      const includedRemaining = Number(accountPlan?.included_usage_remaining || 0);
      const extraRemaining    = Number(accountPlan?.extra_usage_remaining    || 0);
      const walletHasCredits  = includedRemaining > 0 || extraRemaining > 0;
      const freeTierAvailable = !!(fitUsage && Number(fitUsage.remaining) > 0);

      if (isPaidActive && paidExhausted) {
        setUsageExhaustedPlan(accountPlan?.plan_code || 'focus');
        setUsageExhaustedOpen(true);
        return;
      }
      if (!isPaidActive && !freeTierAvailable && !walletHasCredits) {
        // Base user with no free fits AND no wallet credits — show
        // the plan-aware UsageExhaustedModal (Base copy + Add-usage CTA).
        setUsageExhaustedPlan('base');
        setUsageExhaustedOpen(true);
        return;
      }
    }
    setAnalyzing(true);
    // RC 1.49.x Slice 2B — write the in-flight marker BEFORE the
    // network call. If the user closes the tab / navigates away
    // mid-flight, a future mount of this same job's detail page
    // (or a tab return on the still-mounted page) will see the
    // marker and re-fetch the score the backend wrote.
    writeInflightAnalyzeFit(user?.id, id);
    setRehydrationStatus(null);
    try {
      // RC 1.57 Generation Session Guard — get-or-mint the session-
      // scoped idempotency key for THIS (user, job, 'analyse_fit')
      // tuple. A click that lands while a session is still active
      // reuses the SAME key — the backend's `credit_ledger.
      // idempotency_key` partial unique index then dedupes the
      // chargeable POST at the DB. Recovery paths NEVER call this
      // helper — they read `getActiveIdempotencyKey` instead.
      const session = getOrMintSession(user?.id, id, 'analyse_fit');
      const idempotencyKey = session.idempotencyKey;
      // RC 1.57 — implicit `force_reanalyse` REMOVED.
      //
      // Pre-RC 1.57 this block set `force_reanalyse: true` whenever
      // `baselineFit?.baseline` existed — i.e. on every click after
      // the first run. That bypassed the backend's R1 cache short-
      // circuit and produced a fresh charge per click, which the
      // 14 May 2026 audit traced as the secondary cause of duplicate
      // charges.
      //
      // Post-RC 1.57 the flag is OFF by default. Subsequent clicks
      // ride the backend cache lookup (returns the persisted row
      // with `usage_consumed=0`). Genuine re-analysis intent must be
      // expressed through an explicit `resetSession(...)` call from
      // a deliberate UI affordance — none exist today; a future
      // "Force fresh analysis" confirm flow will plug in here.
      const body = primaryId
        ? {
            source_type:     'uploaded_cv',
            source_id:       primaryId,
            idempotency_key: idempotencyKey,
          }
        : { idempotency_key: idempotencyKey };
      // RC 1.48.1 — route through the polling helper so the same call
      // site handles the 200 fast path AND the 202+poll path
      // transparently. Existing callers see the canonical
      // { score, analysis, billing } shape regardless. AbortController
      // ensures unmount during polling does not leave a zombie loop.
      if (analysePollAbortRef.current) {
        analysePollAbortRef.current.abort();
      }
      analysePollAbortRef.current = new AbortController();
      const result = await runAnalyzeFitWithPolling(id, body, {
        signal: analysePollAbortRef.current.signal,
        onRecoveryState: (s) => {
          if (s === 'checking') {
            toast.info("This is taking longer than usual. We're checking your results...");
          } else if (s === 'recovered') {
            toast.success('Your fit analysis was completed in the background.');
          }
        },
      });
      // RC 1.57 — mark terminal success the moment the polling
      // helper resolves with a canonical envelope. The session now
      // enters its stabilisation window — any visibility / remount
      // event inside the next 30 s reads the SAME idempotency key
      // (no fresh mint, no fresh charge).
      markTerminalSuccess(user?.id, id, 'analyse_fit');
      setJob(prev => ({ ...prev, user_score: result.score }));
      // Phase-1 telemetry parity with the wizard's analyze_fit_run schema.
      try {
        const fitVal = result?.analysis?.fit_score ?? result?.score?.fit_score ?? null;
        const newScore = (fitVal != null && fitVal <= 1) ? Math.round(fitVal * 100) : fitVal;
        track('analyze_fit_run', {
          cached: !!result?.billing?.cached,
          new_score: newScore,
          replaced: newScore != null,
          source_type: 'uploaded_cv',
        });
        fireFirstActionOnce('analyze_fit');
      } catch { /* never throw from telemetry */ }
      // RC 1.55 Phase 3 (operator-locked, 2026-05-13) — CANONICAL
      // JOB REBIND. The polling helper returns a thin
      // `{score, analysis, billing}` envelope; merging it into
      // `prev` keeps stale top-level job fields (reasoning_json,
      // final_decision, recommendation, source_filename refresh,
      // updated_at). Fetch the full canonical row so the
      // breakdown card / decision label / helper text / pack
      // readiness all see the same authoritative state the
      // backend just wrote. Fail-open — the merged `prev` above
      // already has the fit_score, so a 5xx here only loses the
      // peripheral fields. CTA stays locked through this fetch
      // because we await it before clearing `analyzing` /
      // `stabilising` below.
      setStabilising(true);
      try {
        // RC 1.59 Slice 3 — explicit cache bypass on the post-
        // mutation re-read. Invalidate first so any other surface
        // that later reads `KEYS.jobById(id)` lands on fresh data;
        // the bare `apiFetch` below is the authoritative re-read
        // for this success path (NOT cached).
        try { cacheInvalidate(KEYS.jobById(id)); } catch { /* noop */ }
        const fresh = await apiFetch(`/api/jobs/${id}`);
        if (fresh) setJob(fresh);
      } catch { /* fail-open — peripheral fields may be stale */ }
      // RC 1.55 Phase 3 — invalidate the dashboard / jobs list
      // cache so queue counters and the Documents library re-
      // derive on next mount. READ-ONLY (no POST). Fail-open.
      // RC 1.59 Slice 3 — extended sweep: fit-usage + plan + this
      // job's baseline-fit may all flip during the Analyse Fit
      // success path. Sweep them so the next mount or page revisit
      // reads fresh server state.
      try {
        cacheInvalidate([
          'jobs',
          'dashboard:bundle',
          KEYS.analyzeFitUsage(),
          KEYS.accountPlan(),
          KEYS.baselineFit(id),
        ]);
      } catch { /* noop */ }
      // RC 1.52 Phase 3B.1 — start the 5-min Analyse Fit cooldown
      // ONLY after a confirmed successful dispatch. Skipped on
      // validation errors / 4xx / 5xx / abort — those land in the
      // catch branch below.
      try {
        const sourceId = primaryId || 'baseline';
        startCooldown(analyseFitCooldownKey(user?.id, id, sourceId));
      } catch { /* noop */ }
      // RC 1.49.x Slice 2B — clear the marker on success. The
      // backend has already written job_scores; the local state
      // now reflects it; the marker is no longer needed.
      clearInflightAnalyzeFit(user?.id, id);
      setRehydrationStatus(null);
      // RC 1.41.F — auto-scroll to the detailed Analyse-Fit
      // breakdown the moment scoring completes. Wrapped in a
      // microtask so React commits the state update first and the
      // FitResultCard exists in the DOM by the time we scroll.
      Promise.resolve().then(() => {
        if (fitBreakdownRef.current) {
          fitBreakdownRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      });
      if (result?.billing?.cached) {
        toast.success('Existing analysis loaded.');
      } else if (result?.billing?.was_free_fit) {
        // Backend may still return this flag during the legacy
        // transition; surface a neutral message that doesn't
        // reference a "monthly free uploaded-CV allowance".
        toast.success('Fit analysis complete.');
      } else if (result?.billing?.usage_consumed === 1) {
        toast.success('Fit analysis complete — 1 included Credit charged.');
      } else {
        toast.success('Fit analysis complete.');
      }
      // RC 1.55 Phase 3 (operator-locked, 2026-05-13) — STABILISATION.
      // Hold the lifecycle-active gate ON until wallet + fit-usage +
      // baseline-fit refreshes settle. Pre-Phase-3 these calls were
      // fire-and-forget so the `finally` flipped `analyzing → false`
      // BEFORE the wallet pill / breakdown strip / baseline refresh
      // committed — leaving a stale spinner-stopped state that
      // operators perceived as "did it fail?". The fix awaits all
      // three in parallel via `Promise.allSettled`, then falls
      // through to `finally`. Failures are individually swallowed
      // (allSettled) so a single 5xx never blocks the gate from
      // closing.
      // (`stabilising` was already set true above before the
      // canonical `/api/jobs/{id}` rebind.)
      await Promise.allSettled([
        (async () => { try { await refreshCredits(); } catch { /* noop */ } })(),
        (async () => { try { await loadFitUsage(); } catch { /* noop */ } })(),
        (async () => {
          try {
            // RC 1.42 P0.2 — refresh baseline-fit so the just-
            // analysed primary CV shows up immediately as the
            // live baseline (and any prior stale row drops out).
            const bf = await apiFetch(`/api/jobs/${id}/baseline-fit`);
            if (bf) setBaselineFit(bf);
          } catch { /* non-fatal */ }
        })(),
      ]);
      // RC 1.57 Generation Session Guard — hydration is now stable
      // (canonical /jobs/{id} rebind committed, wallet refreshed,
      // baseline-fit refreshed). Mark the session as stable and
      // release it ONLY if it is safe to do so (the helper refuses
      // to clear if we are still inside the post-success
      // stabilisation window without confirmed hydration — a
      // belt-and-braces guard against a paint-cycle gap).
      markHydrationStable(user?.id, id, 'analyse_fit');
      releaseSessionIfStable(user?.id, id, 'analyse_fit');
      // RC 1.41 Phase A — monthly free-fit allowance refresh removed.
    } catch (err) {
      // RC 1.48.1 — if polling was aborted (component unmount /
      // navigation / superseded by a new analyse-fit), swallow the
      // abort silently. AbortError is the contract from the polling
      // helper, never a user-visible failure.
      //
      // RC 1.49.x Slice 2B — DO NOT clear the in-flight marker on
      // AbortError. The polling continues server-side; the next
      // mount / tab-return rehydration effect will pick up the
      // saved score and clear the marker.
      if (err?.name === 'AbortError') {
        return;
      }
      // RC 1.60.P0E (operator-approved 2026-02-15) — transient-failure
      // marker preservation. The duplicate-charge production bug
      // closed by RC 1.60.P0D's short-circuit only fires WHEN the
      // marker is present. Forensic trace showed the CDN-504 →
      // recovery-failure path landed in the generic catch branch
      // below and cleared the marker BEFORE the user's impatient
      // second click — opening a fresh-idempotency-key window that
      // P0D could not guard.
      //
      // Mitigation (strict-hold compliant): only clear the marker
      // for DEFINITIVE terminal failures (clean 4xx where the backend
      // rejected the request and no server-side run can complete).
      // Transient errors (5xx, 408, network blips, poll/envelope
      // codes) PRESERVE the marker until the existing 5-min TTL
      // expires — a re-click within that window is short-circuited
      // by P0D rather than minting a fresh billable POST.
      //
      // STRICT-HOLD: no TTL change, no idempotency-key change, no
      // backend / billing / scoring / cooldown / polling-contract /
      // CV / Letter / Prep / Pack changes.
      const _status = err?.status;
      const _code   = err?.code;
      const _msg    = String(err?.message || '');
      const _isTransientFailure = (
        !_status
        || _status >= 500
        || _status === 408
        || _code === 'POLL_TIMEOUT'
        || _code === 'UNEXPECTED_ENVELOPE'
        || _code === 'ASYNC_POLLING_DISABLED'
        || /failed to fetch|network request failed|stream timeout|upstream.*timeout|^timeout/i.test(_msg)
      );
      const code = err?.data?.detail?.code;
      if (err?.status === 429 && code === 'USAGE_EXHAUSTED') {
        loadFitUsage();
        // RC 1.34 — read plan_code off the 429 detail so the modal
        // renders Base vs Focus/Flow copy correctly (base may get
        // here via the extra-usage fallback when purchased extra
        // was present but insufficient).
        setUsageExhaustedPlan(err?.data?.detail?.plan_code || 'focus');
        setUsageExhaustedOpen(true);
        // RC 1.49.x Slice 2B — terminal usage error: clear marker
        // so a tab return doesn't surface a stale "checking…" hint.
        clearInflightAnalyzeFit(user?.id, id);
      } else if (err?.status === 429 && code === 'FREE_DAILY_ANALYZE_LIMIT_REACHED') {
        loadFitUsage();
        setUsageExhaustedPlan('base');
        setUsageExhaustedOpen(true);
        clearInflightAnalyzeFit(user?.id, id);
      } else if (!handleCreditsError(err, { refreshCredits, action: 'fit analysis' })) {
        // RC 1.49.x Slice 2B — humanise generic 5xx / "internal
        // server error" / extraction failure into actionable copy.
        // Targeted backend `detail` strings still pass through.
        const friendly = humaniseAnalyzeFitError(err);
        if (friendly) {
          toast.error(friendly);
        } else {
          // Fallback only for the (very rare) shapes humanise
          // explicitly opts out of — keeps the legacy contract.
          toast.error(err.message || 'Analysis failed.');
        }
        // RC 1.60.P0E — preserve marker on transient failures. The
        // backend run may still complete server-side; the rehydrate
        // path on the next visibility / mount will surface the
        // result. A re-click within the 5-min TTL is short-circuited
        // by P0D rather than minting a fresh chargeable request.
        if (!_isTransientFailure) {
          clearInflightAnalyzeFit(user?.id, id);
        }
      } else {
        // handleCreditsError surfaced its own toast.
        // RC 1.60.P0E — same transient guard applies. handleCreditsError
        // covers wallet 402/429 (almost always definitive); the rare
        // transient that lands here (e.g. wallet-refresh raced) is
        // held until TTL.
        if (!_isTransientFailure) {
          clearInflightAnalyzeFit(user?.id, id);
        }
      }
    } finally {
      // RC 1.55 Phase 3 — release stabilisation AND dispatch gates
      // together so the lifecycle-active strip and CTA-lock clear
      // atomically. The order matters: clearing stabilising first
      // would let the strip transition to the idle text for one
      // commit before disappearing, causing a flicker.
      setStabilising(false);
      setAnalyzing(false);
    }
  };

  // CWS-1 (2026-02-15, operator-locked) — REMOVED: the
  // abort-on-unmount cleanup that ported analyse-fit's RC 1.48.1
  // pattern. The pattern matched the Interview Prep contract
  // BEFORE RC 1.52 Phase 3B.3 fixed it, and produced a split-brain
  // bug: the frontend AbortController fired on unmount (e.g. user
  // navigates to a different workspace section), the polling
  // helper threw AbortError, the handler swallowed it silently,
  // and the backend coroutine (guarded by `asyncio.shield` in the
  // orchestrator) kept running and successfully completed +
  // charged. The user saw the wallet pill drop but no score
  // binding — the dominant symptom in the CANDIDATE_WORKSPACE_LRO
  // _AUDIT.md (causal chain B.1, primary root cause P1).
  //
  // The new contract: polling continues across unmount. React
  // state setters become no-ops on the unmounted component (React
  // dev-warns but does not crash). On remount, the
  // `useOperationMountReconcile` hook (S5b/CWS-3 wiring below)
  // detects the in-flight `operations` row via `/me/active`,
  // extracts `result_envelope_ref` (= analyse_run_id), and resumes
  // polling via `pollAnalyseRun(runId)`. The score binds in
  // `onRecovering`; the rehydrate effect still serves as a
  // fallback for environments where the operations table /
  // mirror-write degraded (e.g. migration not applied).
  //
  // Idempotency at the backend (RC 1.57 `credit_ledger.
  // idempotency_key` UNIQUE + `getOrMintSession`) keeps no-double-
  // charge guarantees intact across all paths.
  //
  // The pre-CWS-1 effect is preserved below for git-blame
  // archaeology and is wrapped in `if (false)` so the linter
  // doesn't strip it. It MUST NOT be re-enabled without re-
  // introducing the audit's P1 bug.
  // eslint-disable-next-line no-constant-condition
  if (false) {
    // useEffect(() => {
    //   return () => {
    //     if (analysePollAbortRef.current) {
    //       analysePollAbortRef.current.abort();
    //       analysePollAbortRef.current = null;
    //     }
    //   };
    // }, [id]);
  }

  /** Enter edit mode: seed the draft from the current job. */
  const beginEdit = () => {
    setDraft(Object.fromEntries(
      EDITABLE_FIELDS.map((f) => [f, job?.[f] ?? '']),
    ));
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };

  /** Build a diff against the loaded job and PATCH only changed fields. */
  const saveEdit = async () => {
    if (!draft) return;
    const body = {};
    for (const f of EDITABLE_FIELDS) {
      const current = job?.[f] ?? '';
      const next = draft[f] ?? '';
      if (String(current) !== String(next)) {
        // Empty string sent as-is; backend treats "" as NULL for most
        // columns, and as an explicit clear for url.
        body[f] = next === '' ? null : next;
      }
    }
    if (Object.keys(body).length === 0) {
      toast.info('No changes to save.');
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const updated = await apiFetch(`/api/jobs/${id}`, { method: 'PATCH', body });
      setJob(updated);
      toast.success('Job updated.');
      setEditing(false);
      setDraft(null);
      // RC 1.59 Slice 3 — bust cached views of this job + the jobs
      // list / dashboard bundle so any later mount sees the fresh
      // row. The PATCH response above already populated local
      // state, so no re-fetch is needed here.
      try {
        cacheInvalidate([KEYS.jobById(id), 'jobs', 'dashboard:bundle']);
      } catch { /* noop */ }
    } catch (err) {
      toast.error(err?.message || 'Failed to update job.');
    } finally {
      setSaving(false);
    }
  };

  const onDraftChange = (field, value) => {
    setDraft((d) => ({ ...d, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="job-detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-ndor-ink-muted" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="text-center py-20" data-testid="job-detail-error">
        <AlertCircle className="h-8 w-8 text-ndor-ink-muted mx-auto mb-3" />
        <p className="text-sm text-ndor-ink-secondary">{error || 'Job not found.'}</p>
        <Link to="/jobs"><Button variant="ghost" className="mt-4">Back to Jobs</Button></Link>
      </div>
    );
  }

  // RC 1.49.x Slice 3 — has a real description? (drives banner copy
  // contract: no truncated snippet — instead, a link that scrolls
  // to the full description card below.)
  const hasFullDescription = !!(
    importedBannerOpen
    && job
    && (job.description || '').replace(/\s+/g, ' ').trim().length > 0
  );

  return (
    <div className="space-y-6 min-w-0 max-w-full overflow-x-hidden" data-testid="job-detail-page">
      {/* Back link */}
      <Link to="/jobs" className="inline-flex items-center gap-1.5 text-sm text-ndor-ink-secondary hover:text-ndor-ink transition-colors" data-testid="job-detail-back">
        <ArrowLeft className="h-4 w-4" /> Back to Jobs
      </Link>

      {/* RC 1.49.x Stabilisation Slice 1 — Imported job ready banner.
          Surfaces the extracted title / company / location / source
          URL / description snippet so the user can verify what was
          parsed before spending credits on Analyze Fit. CTAs:
            • Analyze Fit  → scrolls to the existing Analyse Fit
              section (does NOT auto-charge — operator-locked).
            • Edit details → toggles the existing edit-mode flow.
            • Back to Jobs → returns to the filtered Jobs list.
          Dismiss via the X (also strips ?imported=1 so a refresh
          doesn't re-pop). Hidden once `job` is loaded but the user
          has dismissed, or when no `?imported=1` is in the URL. */}
      {importedBannerOpen && job && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-xl border border-ndor-success/30 bg-ndor-success-light/40 px-4 py-3"
          data-testid="job-detail-imported-banner"
        >
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-ndor-success shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-ndor-ink" data-testid="job-detail-imported-banner-title">
                  Imported job ready
                </p>
                <button
                  type="button"
                  onClick={dismissImportedBanner}
                  className="text-ndor-ink-muted hover:text-ndor-ink transition-colors shrink-0"
                  aria-label="Dismiss imported job banner"
                  data-testid="job-detail-imported-banner-dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-ndor-ink-secondary mt-0.5">
                We extracted these details. Review them before running Analyse Fit.
              </p>
              <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div className="min-w-0">
                  <dt className="text-ndor-ink-muted uppercase tracking-[0.14em] text-[10px] font-mono-ui">Title</dt>
                  <dd className="text-ndor-ink truncate" data-testid="job-detail-imported-banner-job-title">
                    {job.title || 'Untitled Job'}
                  </dd>
                </div>
                {job.company && (
                  <div className="min-w-0">
                    <dt className="text-ndor-ink-muted uppercase tracking-[0.14em] text-[10px] font-mono-ui">Company</dt>
                    <dd className="text-ndor-ink truncate" data-testid="job-detail-imported-banner-company">
                      {job.company}
                    </dd>
                  </div>
                )}
                {job.location && (
                  <div className="min-w-0">
                    <dt className="text-ndor-ink-muted uppercase tracking-[0.14em] text-[10px] font-mono-ui">Location</dt>
                    <dd className="text-ndor-ink truncate" data-testid="job-detail-imported-banner-location">
                      {job.location}
                    </dd>
                  </div>
                )}
                {job.url && (
                  <div className="min-w-0">
                    <dt className="text-ndor-ink-muted uppercase tracking-[0.14em] text-[10px] font-mono-ui">Source URL</dt>
                    <dd className="text-ndor-ink truncate">
                      <a
                        href={job.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-ndor-info hover:underline truncate inline-flex items-center gap-1"
                        data-testid="job-detail-imported-banner-source-url"
                      >
                        <span className="truncate">{job.url}</span>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
              {hasFullDescription ? (
                <button
                  type="button"
                  onClick={scrollToDescription}
                  className="mt-2 inline-flex items-center gap-1 text-xs text-ndor-info hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-ndor-info rounded"
                  data-testid="job-detail-imported-banner-view-description"
                >
                  <ArrowDown className="h-3 w-3 shrink-0" />
                  View full job description below
                </button>
              ) : (
                <p
                  className="text-xs text-ndor-ink-secondary mt-2 leading-relaxed"
                  data-testid="job-detail-imported-banner-no-description"
                >
                  No full description was extracted. You can{' '}
                  <button
                    type="button"
                    onClick={beginEdit}
                    className="text-ndor-info hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-ndor-info rounded"
                  >
                    edit the job details
                  </button>
                  {' '}to add one.
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-ndor-success/40 text-ndor-success hover:bg-ndor-surface"
                  onClick={() => fitBreakdownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  data-testid="job-detail-imported-banner-analyze"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Analyse Fit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={beginEdit}
                  data-testid="job-detail-imported-banner-edit"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit details
                </Button>
                <Button
                  asChild
                  size="sm"
                  variant="ghost"
                  className="text-ndor-ink-secondary"
                  data-testid="job-detail-imported-banner-back"
                >
                  <Link to="/jobs">
                    <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back to Jobs
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RC 1.49.x reviewer-trust polish — the legacy
          NextStepStrip workflow band (Job → Analyse Fit → Tailored
          CV → Cover Letter) was removed from JobDetailPage. The
          mental-model lives on the first-run splash and How It
          Works; the per-job page no longer carries it. */}

      {/* RC 1.25.F — freshness banner for stale / source-404 platform
          rows. Muted wording for "stale" (we haven't observed it in
          three consecutive scans but it may still be live), final
          wording for "source_404" (the adapter told us the posting is
          gone). Deliberately not shown for user_import rows — those
          have no ingestion signal to be confused by. */}
      {job?.source_kind === 'platform' && (job?.hidden_by_source_404 || job?.hidden_by_staleness) && (
        <div
          className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${
            job.hidden_by_source_404
              ? 'border-ndor-error/30 bg-ndor-error-light/40'
              : 'border-ndor-warning/30 bg-ndor-warning-light/40'
          }`}
          data-testid={
            job.hidden_by_source_404
              ? 'job-detail-source-404-banner'
              : 'job-detail-stale-banner'
          }
        >
          <Info className={`h-4 w-4 mt-0.5 shrink-0 ${job.hidden_by_source_404 ? 'text-ndor-error' : 'text-ndor-warning'}`} />
          <div className="text-sm">
            {job.hidden_by_source_404 ? (
              <>
                <p className="text-ndor-ink font-medium">This posting is no longer listed at the source.</p>
                <p className="text-ndor-ink-secondary text-xs mt-0.5">
                  We kept the record for your history, but the company's careers page no longer exposes this role.
                  You can still open the original URL — it may 404.
                </p>
              </>
            ) : (
              <>
                <p className="text-ndor-ink font-medium">This posting may no longer be active.</p>
                <p className="text-ndor-ink-secondary text-xs mt-0.5">
                  We haven't confirmed it in the last three scans of its source. It could still be live — check the
                  original URL before investing effort.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Job header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="space-y-3 max-w-2xl">
              <div>
                <Label className="text-xs text-ndor-ink-muted">Title *</Label>
                <Input
                  value={draft.title || ''}
                  onChange={(e) => onDraftChange('title', e.target.value)}
                  data-testid="job-edit-title"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-ndor-ink-muted">Company</Label>
                  <Input
                    value={draft.company || ''}
                    onChange={(e) => onDraftChange('company', e.target.value)}
                    data-testid="job-edit-company"
                  />
                </div>
                <div>
                  <Label className="text-xs text-ndor-ink-muted">Location</Label>
                  <Input
                    value={draft.location || ''}
                    onChange={(e) => onDraftChange('location', e.target.value)}
                    data-testid="job-edit-location"
                  />
                </div>
                <div>
                  <Label className="text-xs text-ndor-ink-muted">Employment type</Label>
                  <Input
                    value={draft.employment_type || ''}
                    onChange={(e) => onDraftChange('employment_type', e.target.value)}
                    placeholder="e.g. full_time, contract"
                    data-testid="job-edit-employment-type"
                  />
                </div>
                <div>
                  <Label className="text-xs text-ndor-ink-muted">Seniority</Label>
                  <Input
                    value={draft.seniority_level || ''}
                    onChange={(e) => onDraftChange('seniority_level', e.target.value)}
                    placeholder="e.g. senior, lead"
                    data-testid="job-edit-seniority"
                  />
                </div>
                <div>
                  <Label className="text-xs text-ndor-ink-muted">Salary currency</Label>
                  <Input
                    value={draft.salary_currency || ''}
                    onChange={(e) => onDraftChange('salary_currency', e.target.value)}
                    placeholder="e.g. USD, QAR"
                    data-testid="job-edit-salary-currency"
                  />
                </div>
                <div>
                  <Label className="text-xs text-ndor-ink-muted">URL</Label>
                  <Input
                    value={draft.url || ''}
                    onChange={(e) => onDraftChange('url', e.target.value)}
                    placeholder="https://…"
                    data-testid="job-edit-url"
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-semibold tracking-tight text-ndor-ink font-outfit" data-testid="job-detail-title">
                {(!job.title || /^untitled job$/i.test(job.title.trim()))
                  ? (
                    <span
                      className="italic text-ndor-ink-muted cursor-pointer underline-offset-2 hover:underline"
                      onClick={beginEdit}
                      data-testid="job-detail-title-fallback"
                      title="Click to add a title"
                    >
                      Imported job (title not detected — click to edit)
                    </span>
                  )
                  : job.title}
              </h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2 text-sm text-ndor-ink-secondary">
                {job.company && <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4" /> {job.company}</span>}
                {job.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {job.location}</span>}
                {job.employment_type && <Badge variant="outline" className="text-xs capitalize">{job.employment_type.replace('_', ' ')}</Badge>}
                {job.seniority_level && <Badge variant="outline" className="text-xs capitalize">{job.seniority_level}</Badge>}
              </div>
            </>
          )}
        </div>
        <div
          className="flex gap-2 shrink-0 flex-wrap w-full sm:w-auto"
          data-testid="job-detail-action-row"
        >
          {editing ? (
            <>
              <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={saving} data-testid="job-edit-cancel">
                <X className="h-4 w-4 mr-1.5" /> Cancel
              </Button>
              <Button size="sm" onClick={saveEdit} disabled={saving} data-testid="job-edit-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
                Save
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={beginEdit}
                data-testid="job-edit-button"
                className="flex-1 sm:flex-none"
              >
                <Pencil className="h-4 w-4 mr-1.5" /> Edit
              </Button>
              {job.url && (
                <a
                  href={job.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 sm:flex-none"
                >
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                    data-testid="job-view-source-button"
                  >
                    <ExternalLink className="h-4 w-4 mr-1.5" /> View Source
                  </Button>
                </a>
              )}
              <Button
                onClick={requestApply}
                disabled={!job?.user_score || analyzing || rehydrationStatus === 'checking' || rehydrationStatus === 'inflight'}
                title={
                  !job?.user_score
                    ? 'Run Analyse Fit first — Apply opens the workspace once we have a fit score for this job.'
                    : (analyzing || rehydrationStatus === 'checking' || rehydrationStatus === 'inflight')
                      ? 'One moment — finishing your Analyse Fit before opening the workspace.'
                      : 'Open the application workspace — a live session for tailoring your CV, cover letter and interview prep.'
                }
                /* RC 1.56 P2 (operator-locked, 2026-05-13 PM) —
                   Duplicate Apply de-dup on mobile. The sticky
                   bottom Apply CTA below (line 1208) is the
                   authoritative mobile primary. Hiding the inline
                   header Apply on <sm so the user sees ONE primary
                   Apply on mobile (the sticky one) and the inline
                   pair on desktop (where there is no sticky). */
                className="hidden sm:inline-flex w-full sm:w-auto bg-ndor-gold text-ndor-ink hover:bg-ndor-gold/90 font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="job-detail-apply-button"
              >
                <CheckCircle className="h-4 w-4 mr-1.5" />
                Apply to this job
              </Button>
              {!job?.user_score && (
                <p
                  className="w-full sm:w-auto text-[11px] text-ndor-ink-muted sm:max-w-[260px] leading-snug"
                  data-testid="job-detail-apply-locked-hint"
                >
                  Apply opens a workspace where we'll tailor your CV, cover letter and interview prep to this role.{' '}
                  <span className="font-medium text-ndor-ink">Analyse Fit</span> first so we have a fit score to work from.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* RC 1.41 Phase D — sticky bottom Apply CTA on mobile.
          RC 1.56 P2 (operator-locked, 2026-05-13 PM) — additionally
          gated on `!workspaceEntryOpen` so when the
          WorkspaceApplyEntryCard is open below, the sticky Apply
          does NOT cover the card's "Open workspace" / "Cancel"
          footer actions. Mobile users were getting trapped — the
          sticky button sat over the intro card's footer and the
          Open workspace CTA was unreachable without
          scroll-guess. */}
      {!editing && !workspaceEntryOpen && (
        <div className="fixed inset-x-0 bottom-0 z-40 sm:hidden border-t border-ndor-edge bg-ndor-surface/95 backdrop-blur p-3" data-testid="job-detail-apply-sticky">
          <Button
            onClick={requestApply}
            disabled={!job?.user_score || analyzing || rehydrationStatus === 'checking' || rehydrationStatus === 'inflight'}
            title={
              !job?.user_score
                ? 'Run Analyse Fit first — Apply opens the workspace once we have a fit score.'
                : (analyzing || rehydrationStatus === 'checking' || rehydrationStatus === 'inflight')
                  ? 'One moment — finishing your Analyse Fit before opening the workspace.'
                  : 'Open the application workspace.'
            }
            className="w-full bg-ndor-gold text-ndor-ink hover:bg-ndor-gold/90 font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="job-detail-apply-button-mobile"
          >
            <CheckCircle className="h-4 w-4 mr-1.5" />
            {!job?.user_score ? 'Analyse Fit first to Apply' : 'Apply to this job'}
          </Button>
        </div>
      )}

      {/* RC 1.49.7 Slice 6 — inline workspace entry card. Renders
          above the Apply CTAs when the user has clicked Apply AND
          workspace mode is on. Self-contained: collapse → no leak. */}
      {workspaceEntryOpen && (
        <div className="mb-6">
          <WorkspaceApplyEntryCard
            jobId={id}
            onCancel={() => setWorkspaceEntryOpen(false)}
          />
        </div>
      )}

      <ApplyFlowClarifierModal
        open={clarifierOpen}
        onContinue={() => { setClarifierOpen(false); setEntryNoticeOpen(true); }}
        onCancel={() => setClarifierOpen(false)}
      />

      {/* RC 1.49.6 — Wizard Entry Notice. Operator-locked
          guidance dialog rendered immediately before the wizard
          opens on every user-initiated launch. Auto-reopen path
          (L-1 marker) bypasses this notice. */}
      <WizardEntryNotice
        open={entryNoticeOpen}
        onContinue={() => { setEntryNoticeOpen(false); setWizardOpen(true); }}
        onCancel={() => setEntryNoticeOpen(false)}
      />

      <JobApplicationWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        job={job}
        onPackCreated={() => { /* navigation handled inside wizard */ }}
      />

      {/* RC 1.49.x reviewer-trust polish — the "What is an
          Application Pack?" explainer card was removed from
          JobDetailPage. This content now lives on the first-run
          mental-model splash (mounted on /dashboard) and How It
          Works. The per-job page no longer carries the explainer. */}

      {/* RC 1.41.F — top-of-page score summary. Renders BOTH the
          deterministic keyword score (free, computed in-process) and
          the NDOR Analyse-Fit score (paid, set after the user clicks
          "Analyse Fit"). Visible whenever EITHER score is available
          so the user sees their position immediately. The link
          auto-scrolls to the detailed breakdown below. */}
      {/* RC 1.46 — score summary harmonised with the new billing-protection
          model. Renders the LLM-graded NDOR CV verdict (cv_sub_score)
          prominently as the primary commercial-trust signal. The
          deterministic keyword overlap is demoted off this surface
          (kept for audit + appears in the fuller breakdown panel below).
          When no Analyse Fit baseline exists we render an empty verdict
          tile with a strong CTA to run Analyse Fit — converting the
          pre-baseline state into the right next action. */}
      {!editing && (
        <div
          className="rounded-lg border border-ndor-edge bg-ndor-surface px-4 py-3 sm:px-5 sm:py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          data-testid="job-detail-score-summary"
        >
          <div className="flex items-stretch gap-3 sm:gap-5">
            {/* RC 1.48.3 (2026-05-04) — three-context labelling chip.
                Anchors the entire tile to the Source CV baseline so a
                glancing read does not have to infer context from the
                "Analyze Fit with Source CV" card below. The Wizard
                generated-variant card and the Pack Canonical Fit
                panel each carry their own context labels. */}
            <div
              className="flex items-center self-start"
              data-testid="job-detail-score-summary-context-chip"
            >
              <span className="inline-flex items-center rounded-full border border-ndor-edge bg-ndor-surface-alt/60 px-2 py-0.5 text-[9px] uppercase tracking-[0.16em] text-ndor-ink-muted font-mono-ui">
                Source CV · baseline
              </span>
            </div>
            <div className="flex flex-col" data-testid="job-detail-score-summary-cv-verdict">
              <span className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">
                NDOR CV verdict
              </span>
              <span className="text-2xl font-serif-display text-ndor-ink leading-none mt-1">
                {cvSubScore != null
                  ? cvSubScore
                  : <span className="text-ndor-ink-muted">—</span>}
                <span className="text-xs text-ndor-ink-muted ml-1">/100</span>
              </span>
              <span className="text-[10px] text-ndor-ink-muted mt-1">
                {cvSubScore != null
                  ? 'LLM-graded CV quality vs JD'
                  : 'Run Analyse Fit to score this role'}
              </span>
            </div>
            {job?.user_score?.fit_score != null && (
              <>
                <div className="w-px bg-ndor-edge self-stretch" aria-hidden />
                <div className="flex flex-col" data-testid="job-detail-score-summary-fit">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">
                    Overall Fit
                  </span>
                  <span className="text-2xl font-serif-display text-ndor-ink leading-none mt-1">
                    {Math.round(
                      Number(job.user_score.fit_score) <= 1
                        ? Number(job.user_score.fit_score) * 100
                        : Number(job.user_score.fit_score),
                    )}
                    <span className="text-xs text-ndor-ink-muted ml-1">/100</span>
                  </span>
                  <span className="text-[10px] text-ndor-ink-muted mt-1">
                    Holistic candidate–role fit · informational
                  </span>
                </div>
              </>
            )}
          </div>
          {cvSubScore == null ? (
            <button
              type="button"
              onClick={() => fitBreakdownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="rounded-full border border-ndor-gold/50 bg-ndor-gold/10 px-3 py-1.5 text-[11px] text-ndor-gold hover:bg-ndor-gold/20 self-start sm:self-center font-mono-ui uppercase tracking-[0.12em]"
              data-testid="job-detail-score-summary-run-analyse-fit-cta"
            >
              Run Analyse Fit ↓
            </button>
          ) : (
            <button
              type="button"
              onClick={() => fitBreakdownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="text-xs text-ndor-ink-secondary hover:text-ndor-ink underline self-start sm:self-center"
              data-testid="job-detail-score-summary-jump-to-breakdown"
            >
              View detailed breakdown ↓
            </button>
          )}
        </div>
      )}

      <Separator />

      {/* ── Analyze Fit with Source CV — RC 1.21 premium panel ── */}
      {!editing && (
        <Card className="border-ndor-edge bg-ndor-surface brand-card-hover" data-testid="analyze-fit-card">
          <CardHeader className="pb-3 border-b border-ndor-edge">
            <CardTitle className="text-base font-serif-display font-semibold text-ndor-ink flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-ndor-gold" />
              Analyse Fit with Source CV
            </CardTitle>
            <p className="text-[11px] uppercase tracking-[0.2em] text-ndor-ink-muted font-mono-ui mt-1">
              Mission Control · Fit Analysis
            </p>
          </CardHeader>
          <CardContent className="space-y-4 pt-5">
            {/* RC 1.41.G4.A — canonical NDOR Analyse-Fit explainer.
                Placed at the top of the panel so users always see
                what they are paying for before clicking the CTA. */}
            <AnalyseFitExplainer variant="expanded" testIdSuffix="job-detail" />
            {/* RC 1.42 P0.2 — stale-baseline honesty strip. Surfaces
                when a previous Analyse Fit row exists against a CV
                that is no longer primary (e.g. user replaced their
                Source CV after analysing this job). Without this,
                the cached score from an older CV would be displayed
                as if it were the current baseline. */}
            {baselineFit?.stale && !baselineFit?.baseline && (
              <div
                className="rounded-md border border-ndor-warning/40 bg-ndor-warning/5 px-3 py-2 text-xs text-ndor-ink-secondary flex items-start gap-2"
                data-testid="job-detail-stale-baseline-strip"
              >
                <AlertCircle className="h-4 w-4 text-ndor-warning mt-0.5 shrink-0" />
                <span>
                  Previous Analyse Fit: <span className="font-mono-ui tabular-nums">{baselineFit.stale.fit_score}/100</span>
                  &nbsp;against <span className="font-medium">{baselineFit.stale.stale_cv_label}</span>.
                  &nbsp;That score is no longer valid because your Source CV has changed.
                  &nbsp;Run Analyse Fit again on your current Source CV to refresh the diagnostic.
                </span>
              </div>
            )}
            {/* RC 1.49.x SOURCE_CV_REPLACE_OPTION_A — Source-CV-changed
                hint. Renders when the latest cached score on
                `job.user_score.reasoning_json.source_id` was
                computed against a base_cvs row that no longer
                matches the user's current primary CV
                (`baselineFit.primary_cv.id`). Pure client-side
                comparison — no extra backend call, no scoring
                logic touched, no automatic re-run. Calm copy,
                user remains in control of the next Analyse Fit. */}
            {(() => {
              const lastSrc = job?.user_score?.reasoning_json?.source_id;
              const currentPrimary = baselineFit?.primary_cv?.id;
              const isStale =
                !!lastSrc && !!currentPrimary && lastSrc !== currentPrimary;
              if (!isStale) return null;
              return (
                <div
                  className="rounded-md border border-ndor-warning/40 bg-ndor-warning/5 px-3 py-2 text-xs text-ndor-ink-secondary flex items-start gap-2"
                  data-testid="job-detail-source-cv-changed-hint"
                >
                  <AlertCircle className="h-4 w-4 text-ndor-warning mt-0.5 shrink-0" />
                  <span>
                    Your Source CV has changed since this analysis.
                    {' '}Run Analyse Fit again to refresh the diagnostic for this role.
                  </span>
                </div>
              );
            })()}
            {/* RC 1.55 Phase 3 (operator-locked, 2026-05-13) —
                page-level Analyse Fit DISPATCH activity strip.
                Surfaces the active lifecycle (click → polling →
                stabilisation) so the operator sees a prominent
                progress signal alongside the small button-internal
                spinner. Gated to render ONLY when no rehydrate
                state has taken over — the rehydrate strips below
                (`checking` / `inflight` / `recovered` / `expired`)
                supersede this one to avoid duplicate strips. The
                CTA is locked through the same window via the
                button's disabled predicate. Visible from the
                moment `setAnalyzing(true)` commits through the
                canonical /api/jobs GET + the credits/usage/
                baseline refreshes; only the `finally` clears it. */}
            {(analyzing || stabilising) && !rehydrationStatus && (
              <div
                role="status"
                aria-live="polite"
                data-testid="analyze-fit-dispatch-strip"
                data-stabilising={stabilising ? '1' : '0'}
                className="rounded-md border border-ndor-gold/40 bg-ndor-gold/5 px-3 py-2.5 text-xs text-ndor-ink flex items-start gap-2 leading-relaxed"
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0 mt-0.5 text-ndor-gold" />
                <span>
                  {stabilising
                    ? 'Finalising your fit — refreshing wallet and breakdown…'
                    : 'Analysing your fit — feel free to switch tabs. We\u2019ll keep the result safe and update this card the moment it\u2019s ready.'}
                </span>
              </div>
            )}
            {/* RC 1.49.x Slice 2B — Analyse Fit rehydration strip.
                Surfaces the in-flight marker state when the user
                returned to this page after starting an Analyse Fit
                elsewhere. Calm, non-blocking; does not auto-trigger
                any LLM call (operator-locked: Analyse Fit must
                stay user-initiated). */}
            {rehydrationStatus === 'checking' && (
              <div
                role="status"
                aria-live="polite"
                data-testid="analyze-fit-rehydration-checking"
                className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2 text-xs text-ndor-ink flex items-start gap-2"
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0 mt-0.5" />
                <span>Still checking your analysis…</span>
              </div>
            )}
            {rehydrationStatus === 'inflight' && (
              <div
                role="status"
                aria-live="polite"
                data-testid="analyze-fit-rehydration-inflight"
                className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2 text-xs text-ndor-ink-secondary flex items-start gap-2 leading-relaxed"
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0 mt-0.5 text-ndor-ink-muted" />
                <span>
                  Your Analyse Fit is still running in the background. We&apos;ll show the result here as soon as it&apos;s ready — feel free to keep working.
                </span>
              </div>
            )}
            {rehydrationStatus === 'recovered' && (
              <div
                role="status"
                aria-live="polite"
                data-testid="analyze-fit-rehydration-recovered"
                className="rounded-md border border-ndor-success/30 bg-ndor-success-light/40 px-3 py-2 text-xs text-ndor-success flex items-start gap-2"
              >
                <CheckCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>Analysis recovered — your fit score is below.</span>
              </div>
            )}
            {rehydrationStatus === 'expired' && (
              <div
                role="alert"
                data-testid="analyze-fit-rehydration-expired"
                className="rounded-md border border-amber-300 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 flex items-start gap-2 leading-relaxed"
              >
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  We couldn&apos;t confirm your previous Analyse Fit. Your credits were not charged for failed analyses — please try again when ready.
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
              <div className="space-y-1.5" data-testid="job-detail-baseline-source-display">
                <Label className="text-ndor-ink-secondary text-[11px] uppercase tracking-[0.16em] font-mono-ui">
                  Source CV (baseline)
                </Label>
                <p className="text-sm font-medium text-ndor-ink truncate" data-testid="job-detail-primary-cv-name">
                  {baselineFit?.primary_cv?.filename || 'Source CV'}
                </p>
                <p className="text-[11px] text-ndor-ink-muted">
                  Baseline Analyse Fit always uses your current Source CV.
                </p>
              </div>
              <Button
                onClick={() => guardedAnalyseFitAction(handleAnalyzeFit)}
                disabled={
                  analyzing
                  // RC 1.55 Phase 3 (operator-locked, 2026-05-13)
                  // — keep the CTA locked through stabilisation so
                  // the user can't click Re-analyse before the
                  // wallet pill + breakdown card finish refreshing.
                  // Mirrors the page-level dispatch strip's gate.
                  || stabilising
                  || !baselineFit?.primary_cv?.id
                  // RC 1.52 Phase 3B.1 — post-dispatch cooldown.
                  // After a successful Analyse Fit run we lock the
                  // button for 5 min so the user can't double-fire
                  // on a stale wallet pill. Other sources (variants
                  // generated later) are NOT locked because the
                  // cooldown key is sourceId-scoped.
                  || analyseFitCooldown.active
                  // RC 1.55 U4-AF-CTA-LOCK (operator-locked,
                  // 2026-05-13) — additional P0 CTA lock during
                  // lifecycle refresh / rehydrate. While the
                  // `rehydrateAnalyseFit` callback (L321) is
                  // mid-flight OR the background marker says a
                  // run is still in flight, the Re-analyse button
                  // MUST stay disabled so the user can't fire a
                  // duplicate chargeable run on a temporarily
                  // stale wallet/score view. Frontend-only guard.
                  // Strictly orthogonal to scoring + billing +
                  // orchestrator — no dispatch, no idempotency
                  // mutation. The 'recovered' and 'expired'
                  // states do NOT lock — recovered means the
                  // score is fresh and the user may legitimately
                  // re-analyse; expired means the prior run failed
                  // and the user explicitly needs to retry.
                  || rehydrationStatus === 'checking'
                  || rehydrationStatus === 'inflight'
                  // RC 1.34 — when the user is exhausted (paid or
                  // free-tier), keep the button enabled so the click
                  // opens the UsageExhaustedModal / DailyLimitModal
                  // with a visible Add-usage CTA. Disabling silently
                  // would trap the user with no explicit next step.
                }
                data-testid="analyze-fit-button"
                data-cooldown-active={analyseFitCooldown.active ? '1' : '0'}
                data-cooldown-remaining-ms={analyseFitCooldown.active ? String(analyseFitCooldown.remainingMs) : ''}
                data-rehydration-status={rehydrationStatus || ''}
                title={
                  rehydrationStatus === 'checking'
                    ? 'Refreshing analysis — please wait for the current result to settle.'
                    : rehydrationStatus === 'inflight'
                      ? 'Analysis is still updating in the background. We\'ll re-enable this once the result lands.'
                      : analyseFitCooldown.active
                        ? `Analyse Fit is on cooldown for ${analyseFitCooldown.label}. This prevents accidental re-clicks while your wallet syncs.`
                        : undefined
                }
                className="bg-ndor-primary hover:bg-ndor-primary-hover text-ndor-primary-foreground"
              >
                {(analyzing
                  || stabilising
                  || rehydrationStatus === 'checking'
                  || rehydrationStatus === 'inflight')
                  ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  : <Sparkles className="h-4 w-4 mr-1.5" />}
                {rehydrationStatus === 'checking'
                  ? 'Refreshing analysis…'
                  : rehydrationStatus === 'inflight'
                    ? 'Analysis is still updating…'
                    : analyseFitCooldown.active
                      ? `Run again in ${analyseFitCooldown.label}`
                      : (stabilising
                        ? 'Finalising…'
                        : (analyzing ? 'Analysing...' : (baselineFit?.baseline ? 'Re-analyse Fit' : 'Analyse Fit')))}
                {!analyzing
                  && !stabilising
                  && !analyseFitCooldown.active
                  && rehydrationStatus !== 'checking'
                  && rehydrationStatus !== 'inflight'
                  && (
                  <AnalyzeFitCost className="ml-2 text-white/80 text-[11px]" />
                )}
              </Button>
            </div>
            {/* RC 1.31 / RC 1.33 — Usage hint. Paid plans (Focus/Flow)
                show included usage remaining; base plans keep the
                2/day free-tier copy. The reset line is omitted for
                paid plans since their window is tied to the billing
                cycle. */}
            {!job.user_score && (isPaidActive ? (
              <div
                className="flex flex-col gap-0.5 text-xs"
                data-testid="analyze-fit-usage-hint"
              >
                {paidExhausted ? (
                  <span
                    className="text-ndor-warning"
                    data-testid="analyze-fit-usage-exhausted"
                  >
                    You've used your included Credits. Add extra Credits or
                    renew your plan to continue.
                  </span>
                ) : (
                  <span
                    className="text-ndor-ink-secondary"
                    data-testid="analyze-fit-usage-remaining"
                  >
                    Included Credits remaining: {paidTotalRemaining}
                  </span>
                )}
              </div>
            ) : (fitUsage && (
              <div
                className="flex flex-col gap-0.5 text-xs"
                data-testid="analyze-fit-quota-hint"
              >
                {(() => {
                  // RC 1.33.FIX — clamp so a stale response can never
                  // render a negative remaining count.
                  // RC 1.38.A — exhausted-state copy now reflects the
                  // runtime-configured limit (via fitUsage.limit) so it
                  // stays in sync with `app_settings.limits.free_daily_analyze_fit`.
                  const remaining = Math.max(0, fitUsage.remaining || 0);
                  const limit     = Math.max(1, Number(fitUsage.limit) || 2);
                  return remaining > 0 ? (
                    <span
                      className="text-ndor-ink-secondary"
                      data-testid="analyze-fit-quota-remaining"
                    >
                      You have {remaining} free analyses left today.
                    </span>
                  ) : (
                    <span
                      className="text-ndor-warning"
                      data-testid="analyze-fit-quota-reached"
                    >
                      You've used your {limit} free job analyses for today.
                    </span>
                  );
                })()}
                <span
                  className="text-ndor-ink-muted"
                  data-testid="analyze-fit-quota-reset"
                >
                  Your allowance resets at 00:00 UTC.
                </span>
              </div>
            )))}
            {(() => {
              // RC 1.42 P0.2 — replaced the prior multi-branch hint
              // (variant-cost / uploaded-cv-cost / pick-a-cv) with a
              // single clear cost line. Baseline Analyse Fit always
              // targets the current primary CV, so the source is
              // never ambiguous.
              return (
                <p className="text-xs text-ndor-ink-muted flex items-center gap-1.5" data-testid="analyze-fit-source-hint">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-ndor-ink-muted" />
                  Analyse Fit on your Source CV. Includes CV review. Cost: <PriceLabel action="analyze_fit" prefix="" />.
                </p>
              );
            })()}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Job content */}
        <div className="lg:col-span-2 space-y-6">
          {editing ? (
            <>
              <Card className="border-ndor-edge shadow-sm">
                <CardHeader><CardTitle className="text-base font-medium text-ndor-ink">Description</CardTitle></CardHeader>
                <CardContent>
                  <Textarea
                    rows={10}
                    value={draft.description || ''}
                    onChange={(e) => onDraftChange('description', e.target.value)}
                    placeholder="Paste or correct the job description…"
                    data-testid="job-edit-description"
                  />
                </CardContent>
              </Card>
              <Card className="border-ndor-edge shadow-sm">
                <CardHeader><CardTitle className="text-base font-medium text-ndor-ink">Requirements</CardTitle></CardHeader>
                <CardContent>
                  <Textarea
                    rows={8}
                    value={draft.requirements_text || ''}
                    onChange={(e) => onDraftChange('requirements_text', e.target.value)}
                    placeholder="Key requirements, skills, qualifications…"
                    data-testid="job-edit-requirements"
                  />
                </CardContent>
              </Card>
              <p className="text-xs text-ndor-ink-muted">
                Edits update this job record only. Existing fit analyses, CV variants, and
                cover letters are not re-generated — re-run them manually when ready.
              </p>
            </>
          ) : (
            <>
              {job.description && (
                <Card ref={descriptionCardRef} className="border-ndor-edge shadow-sm" data-testid="job-detail-description-card">
                  <CardHeader><CardTitle className="text-base font-medium text-ndor-ink">Description</CardTitle></CardHeader>
                  <CardContent>
                    <JobDescription
                      text={job.description}
                      testid="job-detail-description"
                    />
                  </CardContent>
                </Card>
              )}
              {job.requirements_text && (
                <Card className="border-ndor-edge shadow-sm">
                  <CardHeader><CardTitle className="text-base font-medium text-ndor-ink">Requirements</CardTitle></CardHeader>
                  <CardContent>
                    <JobDescription
                      text={job.requirements_text}
                      testid="job-detail-requirements"
                    />
                  </CardContent>
                </Card>
              )}
              {!job.description && !job.requirements_text && (
                <Card className="border-ndor-edge shadow-sm border-dashed">
                  <CardContent className="p-8 text-center">
                    <p className="text-sm text-ndor-ink-muted">No description available for this job.</p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>

        {/* Right: Fit analysis panel */}
        <div className="space-y-4">
          {/* RC 1.46 — ScoreLedger retired. The harmonised top-of-page
              score-summary tile (NDOR CV verdict + Overall Fit) is now
              the single source of commercial-trust scoring on this
              page. Deterministic keyword overlap is internal-only. */}

          {job.user_score ? (
            <div ref={fitBreakdownRef} data-testid="job-detail-fit-breakdown">
              <FitResultCard score={job.user_score} scope="job" />
              {/* RC 1.50 polish-clarification (2026-02-09 PM) — the
                  base Analyse Fit / Job Detail page is DIAGNOSTIC
                  ONLY and must NOT expose Interview Prep content
                  (likely questions, technical / behavioural /
                  strategic / gap-probe scrutiny, STAR scaffolds).
                  The legacy `<InterviewPrepCard>` mount at this
                  position has been retired per operator product
                  rule: Workspace Interview Prep is a separate
                  paid deliverable, unlocked only when the user
                  clicks "Generate Interview Prep" in the
                  Workspace. The persisted `reasoning_json.interview_prep`
                  payload is still produced by the baseline run as
                  a side effect (back-end unchanged), but it is
                  no longer surfaced here. */}
            </div>
          ) : (
            <Card className="border-ndor-edge shadow-sm border-dashed">
              <CardContent className="p-6 text-center">
                <Sparkles className="h-8 w-8 text-ndor-ink-muted mx-auto mb-3" />
                <p className="text-sm font-medium text-ndor-ink">No analysis yet</p>
                <p className="text-xs text-ndor-ink-muted mt-1">Click "Analyse Fit" to see how well you match this role.</p>
              </CardContent>
            </Card>
          )}

          {/* RC 1.41 Phase E — `DocumentsForJobSection` retired. The
              candidate flow now funnels exclusively through the
              4-step Job Application Wizard mounted above. The
              Documents page (`/documents`) is the read-only library
              for past variants and cover letters. */}
        </div>
      </div>

      {/* RC 1.33 → RC 1.34 — usage exhausted modal. Copy switches
          on the plan_code the backend reports in the 429 detail
          body (base vs focus/flow). Handles both the free-tier
          daily limit (plan='base') and the paid USAGE_EXHAUSTED
          case. The legacy DailyLimitModal was retired in RC 1.34. */}
      <UsageExhaustedModal
        open={usageExhaustedOpen}
        onOpenChange={setUsageExhaustedOpen}
        planCode={usageExhaustedPlan}
      />

      {/* RC 1.58 Phase 3E (operator-locked, 2026-02-14) — Workspace
          Session Guidance modal. Gates the Analyse Fit click; the
          same hook also gates "Open Workspace" via
          `WorkspaceApplyEntryCard`. Shared sessionStorage key so a
          user who acknowledges at either surface never sees it twice
          in the same tab. UI only; no backend impact.
          Phase 3E.A (2026-02-14) — pass `intent="analyse_fit"` so
          the body copy reflects Analyse-Fit-specific guidance
          (existing URL/result recovery already rebinds cleanly
          post-completion). */}
      <WorkspaceAcknowledgementDialog
        {...workspaceAckDialogProps}
        intent="analyse_fit"
      />
    </div>
  );
}
